import { TEAM_ROLES, type TeamRole } from "@sebas/core";
import { Alert, Modal } from "@hakistudio/hakiui";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api";
import { lookupUser, useSetShares, useShares, type Share } from "@/lib/teams";
import { Input, Select } from "@/components/ui/Field";

export function ShareDialog({ noteId, onClose }: { noteId: string; onClose: () => void }) {
  const { data } = useShares(noteId);
  const setShares = useSetShares(noteId);
  const [rows, setRows] = useState<Share[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("viewer");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) setRows(data.shares);
  }, [data]);

  const addPerson = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const found = await lookupUser(email.trim());
      if (rows.some((r) => r.userId === found.userId)) {
        setError("They already have access.");
      } else {
        setRows([...rows, { userId: found.userId, role, displayName: found.displayName, acceptedAt: null }]);
        setEmail("");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    setError(null);
    setShares.mutate(
      rows.map(({ userId, role }) => ({ userId, role })),
      { onSuccess: onClose, onError: (e) => setError(e instanceof ApiError ? e.message : String(e)) },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="Share this note">
      <div className="flex flex-col gap-4">
        <form onSubmit={addPerson} className="flex gap-2">
          <Input
            type="email"
            required
className="min-w-0 flex-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="their@email.com"
          />
          <Select
value={role}
            onChange={(v) => setRole(v as TeamRole)}
            options={TEAM_ROLES.filter((r) => r !== "owner").map((r) => ({ label: r, value: r }))}
          />
          <Button type="submit" variant="secondary" disabled={busy}>Add</Button>
        </form>

        {rows.length === 0 ? (
          <p className="text-sm text-ink-faint">Only you can see this note.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((r) => (
              <li key={r.userId} className="flex items-center gap-2 rounded-2xl bg-sunken px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm text-ink">
                  {r.displayName ?? r.userId}
                  {/* Sharing is an offer: until they accept, the note is not in
                      their list and they cannot open it. Say so, so the owner
                      doesn't assume it landed. */}
                  {r.acceptedAt ? null : (
                    <span className="ml-2 text-xs text-ink-faint">Invited</span>
                  )}
                </span>
                <Select
                  size="sm"
value={r.role}
                  onChange={(v) =>
                    setRows(rows.map((x) => (x.userId === r.userId ? { ...x, role: v as TeamRole } : x)))
                  }
                  options={TEAM_ROLES.filter((t) => t !== "owner").map((t) => ({ label: t, value: t }))}
                />
                <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => setRows(rows.filter((x) => x.userId !== r.userId))}>
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {error ? <Alert variant="danger" title={error} /> : null}

        <p className="text-xs text-ink-faint">
          Everyone you add gets an invitation. The note appears under their
          "Shared with me" once they accept it.
        </p>

        <Button onClick={save} disabled={setShares.isPending}>Save sharing</Button>
      </div>
    </Modal>
  );
}
